import { FC, useCallback, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import query from "../../../query";
import { SportTableProps } from "../index";
import TableMobile from "./TableMobile";
import { useSearchParams } from "react-router-dom";
import {
  BetTicketInfoForDisplay,
  BetTicketInfoPayload,
} from "../../../types/betTicket";
import Drawer from "@mui/material/Drawer";
import mutation from "../../../mutation";
import MatchMarket from "../../../pages/MatchMarket";
import produce from "immer";
import { SportType, Option, BetPriceType } from "../../../types/common";
import { useTranslation } from "react-i18next";
import useToggleState from "../../../hooks/useToggleState";
import { Member } from "../../../types/member";
import { Feed } from "../../../types/feed";
import { getSportFeedQuery, SportKey } from "../../../query/sportFeedFactory";
import { MatchSharePayload } from "../../../types/table";

export type NumberOfMatchValue = { key: string; path: string };

export type MapNumberOfMatchTypeKey = {
  live: NumberOfMatchValue;
  today: NumberOfMatchValue;
  early?: NumberOfMatchValue;
  parlay?: NumberOfMatchValue;
  combo?: NumberOfMatchValue;
};

const SportTableMobile: FC<SportTableProps> = ({
  sport = "football",
  type = "today",
  searchTerm = "",
  onTeamsLoad,
  leagueFilterState,
  onLeaguesLoad,
  onShareMatch,
  sharedMatchId,
}) => {
  const { t } = useTranslation();
  const { isToggled, toggle } = useToggleState();
  const betTypeQuery = query.betType();
  const betType = useQuery(betTypeQuery.key, betTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const profileQuery = query.profile();
  const profile = useQuery(profileQuery.key, profileQuery.queryFn);

  const updateIsBetSlipToggle = useMutation(mutation.updateIsBetSlipToggle, {
    onSuccess: (newIsBetSlipToggle) => {
      queryClient.setQueryData(["isBetSlipToggle"], newIsBetSlipToggle);
    },
  });

  const updateCurrentTabIndex = useMutation(mutation.updateCurrentTabIndex, {
    onSuccess: (index) => {
      queryClient.setQueryData(["currentTabIndex"], index);
    },
  });

  const feedType = type as "today" | "live" | "early";
  const resolvedBetType = betType.data ?? "normal";
  const providerQuery = query.provider();
  const pvd = useQuery(providerQuery.key, providerQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });
  const sportFeedQuery = getSportFeedQuery(
    sport as SportKey,
    feedType,
    resolvedBetType,
    pvd.data,
  );

  const sportFeed = useQuery(sportFeedQuery.key, sportFeedQuery.queryFn);
  const queryClient = useQueryClient();

  const favoriteLeagueIdSet = useMemo(() => {
    const ids = profile.data?.favoriteLeagues?.map((l) => String(l.id)) ?? [];
    return new Set(ids);
  }, [profile.data?.favoriteLeagues]);

  useEffect(() => {
    if (sportFeed.data && sportFeed.data.length > 0) {
      const allLeagueNames = sportFeed.data.map((league) => league.nn.en || "");
      const allTeamNames = sportFeed.data.flatMap((league) =>
        league.m.flatMap((match) => [
          match.n?.en?.h,
          match.n?.en?.a,
          match.n?.th?.h,
          match.n?.th?.a,
        ]),
      );

      const combinedList = [...allLeagueNames, ...allTeamNames].filter(Boolean);
      const uniqueItems = Array.from(new Set(combinedList));
      onTeamsLoad(uniqueItems);

      const leagues = sportFeed.data.map((league) => ({
        label: league.nn.en,
        value: String(league.k),
        favorite: favoriteLeagueIdSet.has(String(league.k)),
      }));
      onLeaguesLoad?.(leagues);
    }
  }, [sportFeed.data, onTeamsLoad, onLeaguesLoad, favoriteLeagueIdSet]);

  const leagueFilteredData = useMemo(() => {
    if (!sportFeed.data) return sportFeed.data;
    if (!leagueFilterState) return sportFeed.data;

    const { isAllSelected, selectedLeagues } = leagueFilterState;
    return sportFeed.data.filter((league) => {
      const id = `${league.k}`;
      return isAllSelected ?
          !selectedLeagues.includes(id)
        : selectedLeagues.includes(id);
    });
  }, [sportFeed.data, leagueFilterState]);

  const filteredData = useMemo(() => {
    const source = leagueFilteredData || [];
    const favoriteLeagueIds = profile.data?.favoriteLeagues?.map(
      (league) => `${league.id}`,
    );

    const sortByFavorite = (
      leagues: Feed[] = [],
      favoriteIds: string[] = [],
    ) => {
      return leagues
        ?.map((league) => ({
          ...league,
          isFavorite: Boolean(favoriteIds?.includes(`${league.k}`)),
        }))
        .sort((a, b) => {
          return (
            a.isFavorite === b.isFavorite ? 0
            : a.isFavorite ? -1
            : 1
          );
        });
    };

    if (sharedMatchId) {
      const result: Feed[] = [];

      source?.forEach((league) => {
        const filteredMatches = league.m.filter(
          (match: any) => String(match.k) === String(sharedMatchId),
        );

        if (filteredMatches.length > 0) {
          result.push({
            ...league,
            m: filteredMatches,
          });
        }
      });

      return sortByFavorite(result, favoriteLeagueIds);
    }

    if (!searchTerm?.trim()) {
      return sortByFavorite(source, favoriteLeagueIds);
    }

    const lowercasedSearchTerm = searchTerm.toLowerCase();
    const result: NonNullable<typeof source> = [];

    source?.forEach((league) => {
      const leagueNameMatches =
        (league.nn.en || "").toLowerCase().includes(lowercasedSearchTerm) ||
        (league.nn.th || "").toLowerCase().includes(lowercasedSearchTerm);

      if (leagueNameMatches) {
        result.push(league);
      } else {
        const filteredMatches = league.m.filter(
          (match: any) =>
            (match.n.en.h || "").toLowerCase().includes(lowercasedSearchTerm) ||
            (match.n.en.a || "").toLowerCase().includes(lowercasedSearchTerm) ||
            (match.n.th.h || "").toLowerCase().includes(lowercasedSearchTerm) ||
            (match.n.th.a || "").toLowerCase().includes(lowercasedSearchTerm),
        );

        if (filteredMatches.length > 0) {
          result.push({
            ...league,
            m: filteredMatches,
          });
        }
      }
    });

    return sortByFavorite(result, favoriteLeagueIds);
  }, [
    leagueFilteredData,
    searchTerm,
    sharedMatchId,
    profile.data?.favoriteLeagues,
  ]);

  const updateBetTicketInfoPayload = useMutation(
    mutation.updateBetTicketInfoPayload,
    {
      onSuccess: (payload) => {
        queryClient.setQueryData(["betTicketInfoPayload"], payload);
      },
    },
  );

  let [_searchParams, setSearchParams] = useSearchParams();
  const pinFavorite = useMutation(mutation.pinFavorite, {
    onSuccess: (payload) => {
      const member = queryClient.getQueryData<Member>(["profile"]);
      const newBetTicketInfoPayload = produce(member, (draft) => {
        if (draft) {
          draft.favoriteTeams = payload.favoriteTeams;
          draft.favoriteLeagues = payload.favoriteLeagues;
        }
      });

      queryClient.setQueryData(["profile"], newBetTicketInfoPayload);
    },
  });

  const onClickPinFavorite = useCallback(
    (payload: { id: string; name: string }, type: "LEAGUE" | "TEAM") => {
      if (profile.data?._id) {
        pinFavorite.mutateAsync({
          memberId: profile.data._id,
          update: payload,
          type,
        });
      }
    },
    [pinFavorite, profile.data],
  );
  const handleClose = useCallback(() => {
    toggle();
    setSearchParams({});
  }, [setSearchParams, toggle]);

  const onClickOddPrice = useCallback(
    async (payload: BetTicketInfoPayload & BetTicketInfoForDisplay) => {
      const betTicketInfoPayloads =
        queryClient.getQueryData<
          (BetTicketInfoPayload & BetTicketInfoForDisplay)[]
        >(["betTicketInfoPayload"]) ?? [];
      const index = betTicketInfoPayloads.findIndex((betTicketInfoPayload) => {
        return betTicketInfoPayload.matchId === payload.matchId;
      });

      const exactMatchIndex =
        index > -1 ?
          betTicketInfoPayloads.findIndex((betTicketInfoPayload) => {
            return (
              betTicketInfoPayload.matchId === payload.matchId &&
              betTicketInfoPayload.option === payload.option &&
              betTicketInfoPayload.riskLevel === payload.riskLevel &&
              betTicketInfoPayload.betType === payload.betType
            );
          })
        : -1;

      if (betType?.data === "normal") {
        if (exactMatchIndex >= 0) {
          await updateBetTicketInfoPayload.mutate([]);
        } else {
          await updateBetTicketInfoPayload.mutate([
            { ...payload, pvd: pvd.data },
          ]);
          updateCurrentTabIndex.mutate(0);
          updateIsBetSlipToggle.mutate(true);
        }
      } else {
        if (index >= 0) {
          const newBetTicketInfoPayload = produce(
            betTicketInfoPayloads,
            (draft) => {
              if (exactMatchIndex >= 0) {
                draft.splice(index, 1);
              } else {
                draft[index] = { ...payload, pvd: pvd.data };
              }
            },
          );
          await updateBetTicketInfoPayload.mutate(newBetTicketInfoPayload);
        } else {
          if (betTicketInfoPayloads.length >= 12) {
            toast.error("This betting is reached the maximun limit");
            return;
          }

          await updateBetTicketInfoPayload.mutate([
            ...betTicketInfoPayloads,
            { ...payload, pvd: pvd.data },
          ]);

          if (betTicketInfoPayloads.length === 11) {
            updateCurrentTabIndex.mutate(0);
            updateIsBetSlipToggle.mutate(true);
          }
        }
      }
    },

    [
      pvd?.data,
      betType?.data,
      queryClient,
      updateBetTicketInfoPayload,
      updateCurrentTabIndex,
      updateIsBetSlipToggle,
    ],
  );

  const handle = (payload1: { leagueId: string; leagueName: string }) => {
    return (payload2: {
      matchId: string;
      homeName: string;
      awayName: string;
      fhid: string; // home flag
      faid: string; // away flag
      fhimg?: string;
      faimg?: string;
    }) => {
      return (payload3: {
        option: Option;
        riskLevel: number;
        betType: BetPriceType;
        isHomeGood: boolean;
        isAwayGood: boolean;
        outcomeId: string;
        specifiers: string;
        marketId: string;
      }) => {
        onClickOddPrice({
          leagueId: payload1.leagueId,
          matchId: payload2.matchId,
          option: payload3.option,
          sportType: sport.toUpperCase() as SportType,
          table: type,
          type: betType?.data ?? "normal",
          betType: payload3.betType,
          home: payload2.homeName,
          away: payload2.awayName,
          fhid: payload2.fhid,
          faid: payload2.faid,
          fhimg: payload2?.fhimg,
          faimg: payload2?.faimg,
          isHomeGood: payload3.isHomeGood,
          isAwayGood: payload3.isAwayGood,
          league: payload1.leagueName,
          riskLevel: payload3.riskLevel,
          outcomeId: payload3.outcomeId,
          specifiers: payload3.specifiers,
          marketId: payload3.marketId,
        });
      };
    };
  };

  const handleShareClick = useCallback(
    ({ leagueId, matchId }: { leagueId: string; matchId: string }) => {
      if (!onShareMatch || !filteredData?.length) return;

      // หา league ที่ตรงกับ leagueId จาก data หลัง filter แล้ว
      const league = filteredData.find((l) => String(l.k) === String(leagueId));
      if (!league) return;

      // หา match ในลีกนั้น
      const match = league.m.find((m: any) => String(m.k) === String(matchId));
      if (!match) return;

      const payload: MatchSharePayload = {
        matchId: String(match.k),
        leagueId: String(league.k),
        leagueName: league.nn?.en ?? "",
        homeName: match.n?.en?.h ?? "",
        awayName: match.n?.en?.a ?? "",
        kickoffTime: (match as any).bt ?? (match as any).mt ?? undefined,
        sportType: sport.toUpperCase() as SportType,
        tableType: type,
      };

      onShareMatch(payload);
    },
    [filteredData, onShareMatch, sport, type],
  );

  return (
    <>
      {filteredData?.length ?
        filteredData?.map((league) => (
          <div key={league.k}>
            <TableMobile
              isFlagVisible={sport === "football"}
              sport={sport}
              league={league}
              onClickPinFavorite={onClickPinFavorite}
              onClickOddPrice={handle}
              onClickMore={toggle}
              type={type}
              onClickShare={onShareMatch ? handleShareClick : undefined}
            />
          </div>
        ))
      : <div className="text-center text-md my-4">{t("No competition")}</div>}

      <Drawer
        open={isToggled}
        onClose={handleClose}
        anchor="bottom"
        classes={{ paper: "rounded-lg" }}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "transparent",
          },
        }}>
        <div style={{ height: "calc(100vh - 56px)" }}>
          <MatchMarket
            onClose={handleClose}
            onClickShare={onShareMatch ? handleShareClick : undefined}
          />
        </div>
      </Drawer>
    </>
  );
};

export default SportTableMobile;
